import path from "node:path";
import { env } from "@/configs/env";
import c from "ansi-colors";
import pino, { type Level, type LoggerOptions } from "pino";
import { match } from "ts-pattern";

const LEVEL_COLORS: Record<Level, c.StyleFunction> = {
  fatal: c.red.bold,
  error: c.red,
  warn: c.yellow,
  info: c.blue,
  debug: c.white,
  trace: c.gray,
};
const LEVEL_CLOG_METHODS: Record<
  Level,
  "log" | "debug" | "info" | "warn" | "error"
> = {
  fatal: "error",
  error: "error",
  warn: "warn",
  info: "info",
  debug: "debug",
  trace: "log",
};

const baseLoggerOptions: LoggerOptions = {
  nestedKey: "payload",
  redact: ["token", "sessionToken", "password"],
  browser: {
    write: (logObj) => {
      const { context, level, module, msg, time, ...payload } = logObj as {
        context: "server" | "client";
        level: Level;
        module: string;
        msg: string;
        time: number;
      };
      const colorFunc = LEVEL_COLORS[level];
      const prefix =
        `${colorFunc(`[${level.toUpperCase()}]`)} ${module ? c.cyan.bold(`[${module}]`) : ""}`.trim();
      console[LEVEL_CLOG_METHODS[level]](
        `${prefix} - ${msg}`,
        ...(Object.keys(payload).length > 0 ? [payload] : []),
      );
    },
    formatters: {
      level: (label) => {
        return {
          level: label,
        };
      },
    },
  },
};

const baseLogger = match<{ PROD: boolean; SSR: boolean }>(import.meta.env)
  .with({ PROD: true, SSR: true }, () => {
    return pino({
      ...baseLoggerOptions,
      level: "debug",
      transport: {
        targets: [
          {
            target: "@highlight-run/pino",
            options: {
              projectID: "7e3yqw6g",
              serviceName: "frontend-bff",
              serviceVersion: env.RAILWAY_GIT_COMMIT_SHA,
            },
            level: "info",
          },
          {
            target: "pino/file",
          },
        ],
      },
    });
  })
  .with({ PROD: true, SSR: false }, () =>
    pino({
      ...baseLoggerOptions,
      level: "debug",
    }),
  )
  .with({ PROD: false, SSR: true }, () =>
    pino({
      ...baseLoggerOptions,
      redact: [],
      transport: {
        target: path.join(process.cwd(), "src/libs/pino-pretty-transport.mjs"),
        options: {
          colorize: true,
        },
      },
      level: "trace",
    }),
  )
  .with({ PROD: false, SSR: false }, () =>
    pino({
      ...baseLoggerOptions,
      redact: [],
      level: "trace",
    }),
  )
  .exhaustive();

export const logger = baseLogger.child({
  module: "root",
  context: import.meta.env.SSR ? "server" : "client",
});
